import { MarketingAttributionMultiTouchModel } from '@server/marketing-attribution';
import {
  apiSweepOperatorWithValueToSweepFieldOperator,
  ApiSweepFieldOperator,
  SweepFieldOperatorsValues,
  sweepFieldOperatorToLabel,
  operatorHasAutomaticValue,
} from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { AutomationType } from '../../../types/enums/AutomationType';
import { DeployStatusForTable } from '../../../types/enums/DeployStatusForTable';

export const MARKETING_ATTRIBUTION_FILTERS = {
  CAMPAIGN: 'campaignFilter',
  CAMPAIGN_MEMBER: 'campaignMemberFilter',
  OPPORTUNITY_CONTACT_ROLE: 'opportunityContactRoleFilter',
} as const;

export const MARKETING_ATTRIBUTION_DISPLAY_NAMES = {
  CAMPAIGN: 'Campaign',
  CAMPAIGN_MEMBER: 'Campaign Member',
  OPPORTUNITY_CONTACT_ROLE: 'Opportunity Contact Role',
  OPPORTUNITY: 'Opportunity',
} as const;

export const getMarketingAttributionObjectList = (
  automationJson: DeepPartial<AutomationStructureNew>,
  attributionFilters: boolean,
) => {
  const objects: string[] = [];

  if (!automationJson.automationDetails?.when?.isCriteria) {
    objects.push(MARKETING_ATTRIBUTION_DISPLAY_NAMES.OPPORTUNITY);
  }

  if (
    !(automationJson.automationDetails?.actions as MarketingAttributionModelAction[])?.[0]
      ?.actionParams?.campaignFilter?.isCriteria
  ) {
    objects.push(MARKETING_ATTRIBUTION_DISPLAY_NAMES.CAMPAIGN);
  }
  if (attributionFilters) {
    // TODO: remove this if when Feature Flag 'attributionFilters' is removed
    if (
      !(automationJson.automationDetails?.actions as MarketingAttributionModelAction[])?.[0]
        ?.actionParams?.campaignMemberFilter?.isCriteria
    ) {
      objects.push(MARKETING_ATTRIBUTION_DISPLAY_NAMES.CAMPAIGN_MEMBER);
    }
    if (
      !(automationJson.automationDetails?.actions as MarketingAttributionModelAction[])?.[0]
        ?.actionParams?.opportunityContactRoleFilter?.isCriteria
    ) {
      objects.push(MARKETING_ATTRIBUTION_DISPLAY_NAMES.OPPORTUNITY_CONTACT_ROLE);
    }
  }
  return objects;
};

export const getMarketingAttributionAction = (
  automationJson: DeepPartial<AutomationStructureNew>,
) => {
  const action = automationJson.automationDetails?.actions as MarketingAttributionAction[];
  return action?.[0];
};

export const getMarketingAttributionActionParams = (
  automationJson: DeepPartial<AutomationStructureNew>,
) => {
  const action = getMarketingAttributionAction(automationJson);
  return action?.actionParams;
};

export const getMarketingAttributionWeights = (
  automationJson: DeepPartial<AutomationStructureNew>,
) => {
  const actionParams = getMarketingAttributionActionParams(automationJson);
  return (actionParams as MarketingAttributionMultiTouchModel)?.weights;
};

export const getMarketingAttributionModelType = (
  automationJson: DeepPartial<AutomationStructureNew>,
) => {
  const actionParams = getMarketingAttributionActionParams(automationJson);
  return actionParams?.modelType;
};

export const getMarketingAttributionFilter = (
  automationJson: DeepPartial<AutomationStructureNew>,
  filterProperty: string,
) => {
  const actionParams = getMarketingAttributionActionParams(automationJson);
  return actionParams?.[filterProperty as keyof typeof actionParams] as
    | (SweepCondition & { isCriteria: boolean })
    | undefined;
};

export const getStageId = (automationJson: DeepPartial<AutomationStructureNew>) => {
  return automationJson.automationDetails?.automationParams?.fieldUpdate?.operatorValue;
};

export const translateModelType = (modelType: string) => {
  switch (modelType) {
    case 'FIRST_TOUCH':
      return 'First Touch';
    case 'LAST_TOUCH':
      return 'Last Touch';
    case 'EVEN_DISTRIBUTION':
      return 'Even Distribution';
    case 'MULTI_TOUCH':
      return 'Multi Touch';
  }
};

const translateOperator = (operator: string, value?: string) => {
  const sweepFieldOperator = apiSweepOperatorWithValueToSweepFieldOperator(
    operator as ApiSweepFieldOperator,
    value as SweepFieldOperatorsValues,
  );
  return sweepFieldOperatorToLabel(sweepFieldOperator);
};

const translateValue = (value: string, operator: ApiSweepFieldOperator) => {
  const isAutomatic = operatorHasAutomaticValue(operator);
  if (isAutomatic) {
    return '';
  }
  return value;
};

export const translateConditionsToString = (
  criteria: any[],
  getLabelsFromIds: (ids: string[]) => string[],
): string[] => {
  return criteria.map(
    (item) =>
      `${getLabelsFromIds(item._fieldIds)?.join(' > ')} ${translateOperator(item.operator, item.value)} ${translateValue(item.value, item.operator)}`,
  );
};

export const isMarketingAttributionModelTemplate = (
  automationJson: DeepPartial<AutomationStructureNew>,
) => {
  return (
    automationJson.createdAt === automationJson.updatedAt &&
    automationJson.type === AutomationType.MarketingAttributionModel &&
    automationJson.status === DeployStatusForTable.Draft
  );
};

export const subtitle = (modelType: string) => {
  switch (modelType) {
    case 'FIRST_TOUCH':
      return 'Attribution is assigned to the first touchpoint';
    case 'LAST_TOUCH':
      return 'Attribution is assigned to the last touchpoint';
    case 'EVEN_DISTRIBUTION':
      return 'Attribution is equally distributed across all touchpoints';
    case 'MULTI_TOUCH':
      return 'Manually define how attribution is distributed across touchpoints ';
  }
};
